import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    Button,
    Flex,
    Stack
} from "@chakra-ui/react";
import { AddIcon } from "@chakra-ui/icons";
import { FaEdit, FaPrint, FaTrashAlt } from "react-icons/fa";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import ListPageCardHeader from "components/Card/ListPageCardHeader";
import ConfirmDialog from "components/Dialog/ConfirmDialog";
import SearchTable2 from "components/Tables/SearchTable2";
import Loading from "components/Loading/Loading";
import { useCustomToast } from "components/CustomToast/CustomToast";
import { ROUTES } from "constants/routes-path";
import { documentsParkingCredentialsService } from "services/documentsParkingCredentialsService";
import { getEmptyPageResponse, toTablePageableData } from "utils/utils";
import { parkingCredentialsService } from "services/parkingCredentialsService";
import ParkingCredentialsDocumentsListPageCardBeneficiaryHeader from "./ParkingCredentialsDocumentsListPageCardBeneficiaryHeader";
import { ParkingCredentialsDocumentsFilter } from "./ParkingCredentialsDocumentsFilter";
import { PARKING_CREDENTIALS_DOCUMENTS_LIST_URL_DEFAULTS } from "utils/listUrlSync";
import { useListUrlSync } from "hooks/useListUrlSync";
import { ListFiltersActiveAlert } from "components/ListFiltersActiveAlert/ListFiltersActiveAlert";

const ParkingCredentialsDocumentsList = () => {
    const { t } = useTranslation();
    const { searchParams, setSearchParams, hasActiveFilters, urlSearchParams } = useListUrlSync(
        PARKING_CREDENTIALS_DOCUMENTS_LIST_URL_DEFAULTS,
        { preserveParentId: true }
    );
    const navigate = useNavigate();
    const showToast = useCustomToast();
    const dialogRef = useRef(undefined);
    const [response, setResponse] = useState(undefined);
    const [beneficiaryData, setBeneficiaryData] = useState(undefined);
    const [selectedItem, setSelectedItem] = useState(null);
    const parentId = urlSearchParams.get("parent_id");

    const columns = useMemo(() => ([
        {
            Header: t("parking-credentials.documents.list.table.header.type"),
            accessor: "tipo"
        },
        {
            Header: t("parking-credentials.documents.list.table.header.issuing-renewal-date"),
            accessor: "data_criacao"
        },
        {
            Header: t("parking-credentials.documents.list.table.header.expiration-date"),
            accessor: "data_validade"
        },
        {
            Header: t("parking-credentials.documents.list.table.header.issued-by"),
            accessor: "nome_usuario_atendente"
        },
        {
            Header: t("parking-credentials.documents.list.table.header.operations"),
            disableSortBy: true,
            actions: [
                {
                    label: t('system.actions.edit'),
                    onClick: (row) =>
                        navigate(`${ROUTES.parkingCredentialsDocumentsEditPath}/${row.original.id}?parent_id=${parentId}`, {
                            state: { parentId }
                        }),
                    icon: <FaEdit />
                },
                {
                    label: t('system.actions.print'),
                    onClick: (row) => console.log(row.original.id),
                    icon: <FaPrint />
                },
                {
                    label: t('system.actions.remove'),
                    onClick: (row) => handleDeleteClick(row.original.id),
                    icon: <FaTrashAlt color="#E53E3E" />,
                }
            ]
        },
    ]), [t, navigate, parentId]);

    const handleDeleteClick = (itemId) => {
        setSelectedItem(itemId);
        dialogRef.current?.displayDialog({
            title: t("documents.list.actions.remove"),
            message: t("system.actions.remove-confirm"),
        });
    };

    const handleConfirmDelete = async () => {
        try {
            await documentsParkingCredentialsService.delete(selectedItem);
            showToast({ title: t('documents.messages.success.delete'), status: "success" });
            fetchData();
        } catch (error) {
            showToast({ title: t(error), status: "error" });
        }
    };

    const fetchData = async (params = {}) => {
        const beneficiaryInfo = await parkingCredentialsService.getBeneficiaryByCredentialId(parentId);
        setBeneficiaryData(beneficiaryInfo);

        params.orderBy = params.orderBy ?? "data_criacao";
        params.page = Math.min(params?.page ?? 1, response?.total_pages ?? Infinity) || 1;
        if (response) setResponse(undefined);

        try {
            params = { ...searchParams, ...params };
            setSearchParams(params);
            const data = await documentsParkingCredentialsService.listWithFilters(params, parentId);
            setResponse(data);
        } catch (err) {
            setResponse(getEmptyPageResponse(searchParams));
            showToast({ title: t(err), status: "error" });
        }
    };

    const clearParkingCredentialsDocumentsFilters = () => {
        fetchData({
            page: 1,
            filters: { data_inicial: null, data_final: null },
        });
    };

    useEffect(() => {
        fetchData();
    }, [parentId]);

    return (
        <Flex direction="column" gap="5" pt={{ base: "150px", lg: "90px" }}>
            <Flex w="100%" px="30px" justify="space-between" align="center" gap="4" direction={{ sm: "column", md: "row" }}>
                <Button
                    w="160px"
                    variant="list-primary"
                    leftIcon={<AddIcon fontSize="sm" />}
                    alignSelf={{ sm: "flex-start", lg: null }}
                    onClick={() => {
                        navigate(`${ROUTES.parkingCredentialsDocumentsAddPath}?parent_id=${parentId}`, {
                            state: { parentId }
                        });
                    }}
                >
                    {t("documents.list.actions.new")}
                </Button>
                <Stack direction="row" spacing="10px" alignSelf={{ sm: "flex-start", lg: "auto" }}>
                    <ParkingCredentialsDocumentsFilter
                        filterSnapshot={searchParams.filters}
                        onFilter={(filters) => fetchData({ filters })}
                    />
                </Stack>
            </Flex>

            <Card overflowX={{ sm: "scroll", lg: "hidden" }}>
                {response ? (
                    <>
                        <ParkingCredentialsDocumentsListPageCardBeneficiaryHeader beneficiaryData={beneficiaryData} />
                        <ListPageCardHeader title={t("documents.title")} subtitle={t("documents.description")} />
                        <CardBody>
                            <ListFiltersActiveAlert show={hasActiveFilters} onClear={clearParkingCredentialsDocumentsFilters} />
                            <SearchTable2
                                columnsData={columns}
                                tableData={response.content}
                                pageableData={toTablePageableData(response, {
                                    defaultField: "data_criacao",
                                    orderBy: searchParams.orderBy ?? "data_criacao",
                                    orderDirection: searchParams.orderDirection ?? "desc",
                                    ...searchParams,
                                })}
                                onChange={fetchData}
                                disableSearch={true}
                            />
                        </CardBody>
                    </>
                ) : (
                    <Loading maxH={{ sm: "100px", md: "350px" }} />
                )}
            </Card>

            <ConfirmDialog ref={dialogRef} onConfirm={handleConfirmDelete} confirmButtonProps={{ colorScheme: "red" }} />
        </Flex>
    );
}

export default ParkingCredentialsDocumentsList;
